import {
  IFarmInputProductDepotInfoInput,
  IDepot,
  IFarmInputProductDepotInfo,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {Controller, useForm} from 'react-hook-form';
import React, {useEffect, useState} from 'react';
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {CalculatePriceDialog} from 'components/ProductForm/CalculatePriceDialog';
import {CalculatePriceFormSubmission} from 'components/ProductForm/CalculatePriceForm';

export function InventoryForm(props: {
  user: ISupplierUserProfile;
  productId: string;
  depotIds: string[];
  allDepots: IDepot[];
  productsDepotInfo: IFarmInputProductDepotInfo[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (productDepotInfo: IFarmInputProductDepotInfoInput[]) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  smallWidth?: boolean;
  readonly?: boolean;
}) {
  const {isLoading} = props;

  const [selectedDepots, setSelectedDepots] = useState<IDepot[]>([]);
  const [showPriceDialog, setShowPriceDialog] = useState(false);

  useEffect(() => {
    const selectedDepots = props.depotIds.map(
      (id) => props.allDepots.find((depot) => depot.id.toString() === id)!,
    );
    setSelectedDepots(selectedDepots);
  }, [props.depotIds, props.allDepots]);

  const getDefaultValues = () => {
    // if only editing for one depot, return depot values
    if (props.depotIds?.length === 1) return props.productsDepotInfo[0];

    // if some of the depots selected have no info, return empty form
    if (props.depotIds.length !== props.productsDepotInfo.length)
      return {isActive: true};

    // else check field by field to determine default values

    const allValuesEqual = (field: string, value: any) => {
      // @ts-ignore
      return props.productsDepotInfo.every((item) => item[field] === value);
    };

    const getDefaultValueAtField = (field: string) => {
      const firstItem = props.productsDepotInfo?.[0];
      // @ts-ignore
      return allValuesEqual(field, firstItem?.[field])
        ? // @ts-ignore
          firstItem?.[field]
        : undefined;
    };

    return {
      isActive: getDefaultValueAtField('isActive'),
      stockOnHand: getDefaultValueAtField('stockOnHand'),
      minOrderQuantityFreeDelivery: getDefaultValueAtField(
        'minOrderQuantityFreeDelivery',
      ),
      minOrderQuantity: getDefaultValueAtField('minOrderQuantity'),
      price: getDefaultValueAtField('price'),
    };
  };

  const {handleSubmit, control, setValue} =
    useForm<IFarmInputProductDepotInfoInput>({
      defaultValues: getDefaultValues(),
    });

  const onSubmit = (input: IFarmInputProductDepotInfoInput) => {
    const updates: IFarmInputProductDepotInfoInput[] = selectedDepots.map(
      (depot) => ({
        isActive: input.isActive === undefined ? true : input.isActive,
        stockOnHand: input.stockOnHand,
        minOrderQuantityFreeDelivery: input.minOrderQuantityFreeDelivery,
        minOrderQuantity: input.minOrderQuantity,
        price: parseFloat(input.price as any) || undefined,
        costPrice: parseFloat(input.costPrice as any) || undefined,
        depotId: depot.id.toString(),
        productId: props.productId,
      }),
    );
    props.onSubmit(updates);
  };

  const handlePriceCalculation = (
    calculation: CalculatePriceFormSubmission,
  ) => {
    setValue('price', calculation.sellingPrice, {shouldTouch: true});
    setValue('costPrice', calculation.costPrice, {shouldTouch: true});
    setShowPriceDialog(false);
  };

  return (
    <div className="generic-form-container inventory-form">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          {props.user?.company?.depotPriceList && (
            <Grid container spacing={2} className="form-section">
              <Grid item sm={12}>
                <div className="price-title-container">
                  <Typography variant={'subtitle2'} gutterBottom>
                    Depot Pricing
                  </Typography>
                  {!props.readonly && (
                    <Button
                      variant="text"
                      onClick={() => setShowPriceDialog(true)}
                      size="small"
                      color={'primary'}
                    >
                      Calculate Price
                    </Button>
                  )}
                </div>
              </Grid>
              <Grid item xs={props.smallWidth ? 12 : 6}>
                <Controller
                  name="price"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <TextField
                      className="input"
                      label="Selling Price"
                      variant="outlined"
                      fullWidth
                      type="number"
                      InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      disabled={props.readonly || isLoading}
                    />
                  )}
                  rules={{
                    required: 'Selling price required',
                    validate: (value) =>
                      !isNaN(value!) || 'Selling price should be a number',
                  }}
                />
              </Grid>
              {!props.readonly && (
                <Grid item xs={props.smallWidth ? 12 : 6}>
                  <Controller
                    name="costPrice"
                    control={control}
                    render={({
                      field: {onChange, value},
                      fieldState: {error},
                    }) => (
                      <TextField
                        className="input"
                        label="Cost Price (Optional)"
                        variant="outlined"
                        fullWidth
                        type="number"
                        InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                        value={value}
                        onChange={onChange}
                        error={!!error}
                        helperText={error ? error.message : null}
                        disabled={isLoading}
                      />
                    )}
                    rules={{
                      validate: (value) =>
                        value === undefined ||
                        !isNaN(value!) ||
                        'Cost price should be a number',
                    }}
                  />
                </Grid>
              )}
            </Grid>
          )}
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Stock
              </Typography>
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="stockOnHand"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Stock On Hand (SOH) (Optional)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                    value={value}
                    onChange={(value) =>
                      onChange(parseFloat(value.target.value))
                    }
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Delivery Details
              </Typography>
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="minOrderQuantity"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Minimum Order Quantity (MOQ) (Optional)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                    value={value}
                    onChange={(value) =>
                      onChange(parseFloat(value.target.value))
                    }
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="minOrderQuantityFreeDelivery"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Minimum Free Delivery Quantity (Optional)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                    value={value}
                    onChange={(value) =>
                      onChange(parseFloat(value.target.value))
                    }
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Depots
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                className="depot-select"
                multiple
                value={selectedDepots}
                options={props.allDepots}
                getOptionLabel={(depot: IDepot) => depot.name}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="medium"
                    variant="outlined"
                    label="Depots"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <div className={'row'}>
                          {selectedDepots.length !== props.allDepots.length && (
                            <Button
                              size={'small'}
                              variant={'text'}
                              color={'inherit'}
                              onClick={() => setSelectedDepots(props.allDepots)}
                            >
                              All
                            </Button>
                          )}
                          {params.InputProps.endAdornment}
                        </div>
                      ),
                    }}
                  />
                )}
                onChange={(event, selectedDepots: IDepot[]) => {
                  setSelectedDepots(selectedDepots);
                }}
              />
            </Grid>
          </Grid>
          {!props.readonly && (
            <div className="btns row" style={{marginTop: '1rem'}}>
              {props.cancelButtonTitle && (
                <Button
                  className={'secondary-btn'}
                  variant="outlined"
                  disabled={props.isLoading}
                  onClick={props.onCancel}
                  disableElevation
                >
                  {props.cancelButtonTitle}
                </Button>
              )}
              <Button
                className={'primary-btn'}
                type="submit"
                variant="outlined"
                color="primary"
                disabled={props.isLoading}
                disableElevation
              >
                {props.isLoading ? (
                  <CircularProgress color={'primary'} size={24} thickness={6} />
                ) : (
                  props.submitButtonTitle
                )}
              </Button>
            </div>
          )}
        </form>
      </div>
      <CalculatePriceDialog
        user={props.user}
        open={showPriceDialog}
        onClose={() => setShowPriceDialog(false)}
        onSubmit={handlePriceCalculation}
      />
    </div>
  );
}
